import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioGrid = ({ projects, activeCategory, categories }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  
  const currentCategory = categories?.find(cat => cat?.id === activeCategory);
  const filteredProjects = projects?.filter(project => project?.category === activeCategory);

  const openModal = (project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  const getGridClass = () => {
    switch (activeCategory) {
      case 'events':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
      case 'realestate':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4';
      case 'marketing':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';
      case 'design':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10';
      default:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
    }
  };

  const getCardClass = () => {
    switch (activeCategory) {
      case 'events':
        return 'rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 bg-white';
      case 'realestate':
        return 'rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 bg-white';
      case 'marketing':
        return 'rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-700 bg-gradient-to-br from-white to-gray-50';
      case 'design':
        return 'rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-1000 bg-black border border-gray-800';
      default:
        return 'rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 bg-white';
    }
  };

  return (
    <>
      <div className={`grid ${getGridClass()} animate-fade-in`}>
        {filteredProjects?.map((project, index) => (
          <div
            key={project?.id}
            className={`${getCardClass()} cursor-pointer transform hover:scale-105`}
            onClick={() => openModal(project)}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="relative aspect-video overflow-hidden">
              <Image
                src={project?.thumbnail}
                alt={project?.title}
                className="w-full h-full object-cover"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${
                activeCategory === 'design' ?'from-black/80 via-transparent to-transparent' :'from-black/50 via-transparent to-transparent'
              }`} />
              <div className="absolute top-4 right-4">
                <div className={`p-2 rounded-full ${
                  activeCategory === 'design' ? 'bg-white/20' : 'bg-black/20'
                } backdrop-blur-sm`}>
                  <Icon 
                    name="Play" 
                    size={20} 
                    color={activeCategory === 'design' ? '#00D4AA' : 'white'} 
                  />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className={`text-lg font-semibold mb-1 ${
                  activeCategory === 'design' ? 'text-white' : 'text-white'
                }`}>
                  {project?.title}
                </h3>
                <p className={`text-sm opacity-90 ${
                  activeCategory === 'design' ? 'text-gray-300' : 'text-gray-200'
                }`}>
                  {project?.client}
                </p>
              </div>
            </div>
            
            <div className={`p-6 ${activeCategory === 'design' ? 'bg-gray-900' : 'bg-white'}`}>
              <p className={`text-sm mb-4 ${
                activeCategory === 'design' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {project?.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {project?.tags?.slice(0, 2)?.map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 text-xs rounded-full ${
                        activeCategory === 'design' ?'bg-gray-800 text-gray-300 border border-gray-700' :'bg-gray-100 text-gray-700'
                      }`}
                      style={{
                        backgroundColor: activeCategory !== 'design' ? `${currentCategory?.accentColor}20` : undefined,
                        color: activeCategory !== 'design' ? currentCategory?.accentColor : undefined
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon 
                    name="Eye" 
                    size={16} 
                    color={activeCategory === 'design' ? '#9CA3AF' : '#6B7280'} 
                  />
                  <span className={`text-sm ${
                    activeCategory === 'design' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {project?.views?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className={`relative max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl ${
            activeCategory === 'design' ? 'bg-gray-900' : 'bg-white'
          }`}>
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40 transition-colors"
            >
              <Icon name="X" size={24} color="white" />
            </button>
            
            <div className="aspect-video relative">
              <Image
                src={selectedProject?.thumbnail}
                alt={selectedProject?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-white/30"
                >
                  Watch Full Video
                </Button>
              </div>
            </div>
            
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className={`text-3xl font-bold mb-2 ${
                    activeCategory === 'design' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {selectedProject?.title}
                  </h2>
                  <p className={`text-lg ${
                    activeCategory === 'design' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {selectedProject?.client}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm ${
                    activeCategory === 'design' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Duration: {selectedProject?.duration}
                  </p>
                  <p className={`text-sm ${
                    activeCategory === 'design' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Views: {selectedProject?.views?.toLocaleString()}
                  </p>
                </div>
              </div>
              
              <p className={`text-base mb-6 leading-relaxed ${
                activeCategory === 'design' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {selectedProject?.fullDescription}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    activeCategory === 'design' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Project Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={activeCategory === 'design' ? 'text-gray-400' : 'text-gray-600'}>
                        Category:
                      </span>
                      <span className={activeCategory === 'design' ? 'text-gray-200' : 'text-gray-800'}>
                        {currentCategory?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={activeCategory === 'design' ? 'text-gray-400' : 'text-gray-600'}>
                        Year:
                      </span>
                      <span className={activeCategory === 'design' ? 'text-gray-200' : 'text-gray-800'}>
                        {selectedProject?.year}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={activeCategory === 'design' ? 'text-gray-400' : 'text-gray-600'}>
                        Location:
                      </span>
                      <span className={activeCategory === 'design' ? 'text-gray-200' : 'text-gray-800'}>
                        {selectedProject?.location}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    activeCategory === 'design' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject?.tags?.map((tag) => (
                      <span
                        key={tag}
                        className={`px-3 py-1 text-sm rounded-full ${
                          activeCategory === 'design' ?'bg-gray-800 text-gray-300 border border-gray-700' :'bg-gray-100 text-gray-700'
                        }`}
                        style={{
                          backgroundColor: activeCategory !== 'design' ? `${currentCategory?.accentColor}20` : undefined,
                          color: activeCategory !== 'design' ? currentCategory?.accentColor : undefined
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  variant="default"
                  iconName="ExternalLink"
                  iconPosition="right"
                  className={`${
                    activeCategory === 'design' ?'bg-[#00D4AA] hover:bg-[#00D4AA]/90 text-black' :''
                  }`}
                  style={{
                    backgroundColor: activeCategory !== 'design' ? currentCategory?.accentColor : undefined
                  }}
                >
                  View Case Study
                </Button>
                <Button
                  variant="outline"
                  iconName="Share2"
                  iconPosition="left"
                  className={activeCategory === 'design' ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : ''}
                >
                  Share Project
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PortfolioGrid;